import { Bot, ChevronRight, Clock, Hourglass, PhoneIncoming, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { cn } from '@/lib/utils'
import type { IvrFlowDetail, IvrNode } from '@/api/ivrFlows'

/**
 * Résumé lisible d'un flux d'appel : « Heures d'ouverture → Toute l'équipe »,
 * « Hors des heures → Agent iii », « Personne ne décroche → messagerie ».
 * Lu depuis les nœuds et les arêtes du flux : ce que le client voit ici est
 * ce qui se passe vraiment, y compris pour un flux dessiné à la main.
 */
export interface Etape {
  quand: string
  cible: string
  ia?: boolean
}

interface Noms {
  equipe: (id: unknown) => string | null
  agent: (id: unknown) => string | null
}

export function decrireFlux(flux: IvrFlowDetail, noms: Noms, t: TFunction): Etape[] {
  const nodes = new Map(flux.nodes.map((n) => [n.node_id, n]))
  const sortie = (node: IvrNode, handle?: string): IvrNode | undefined => {
    const e = flux.edges.find((x) => x.source_node_id === node.node_id && (handle == null || (x.source_handle || '') === handle))
    return e?.target_node_id ? nodes.get(e.target_node_id) : undefined
  }
  const premiereSortie = (node: IvrNode) => sortie(node) ?? sortie(node, '')

  const etapes: Etape[] = []
  const vus = new Set<string>()

  const cibleDe = (node: IvrNode | undefined): { texte: string; ia?: boolean; suite?: IvrNode; suiteQuand?: string } => {
    if (!node) return { texte: t('manager.numbers.who.hangup', 'Raccroche') }
    const cfg = node.config || {}
    switch (node.node_type) {
      case 'route_team': {
        const nom = cfg.team_id === 'all' || !cfg.team_id
          ? t('manager.numbers.who.everyone', "Toute l'équipe")
          : noms.equipe(cfg.team_id) ?? node.label
        const sec = Number(cfg.ring_timeout) || 20
        return {
          texte: t('manager.numbers.who.ringsFor', '{{name}} (sonne {{sec}} s)', { name: nom, sec }),
          suite: sortie(node, 'no_answer'),
          suiteQuand: t('manager.numbers.who.nobodyAnswers', 'Personne ne décroche'),
        }
      }
      case 'queue': {
        const nom = cfg.team_id === 'all' || !cfg.team_id ? null : noms.equipe(cfg.team_id)
        const min = Math.round((Number(cfg.max_wait) || 120) / 60)
        return {
          texte: nom
            ? t('manager.numbers.who.queueTeam', "File d'attente pour {{name}} ({{min}} min max)", { name: nom, min })
            : t('manager.numbers.who.queue', "File d'attente ({{min}} min max)", { min }),
          suite: sortie(node, 'timeout'),
          suiteQuand: t('manager.numbers.who.nobodyFrees', 'Personne ne se libère'),
        }
      }
      case 'route_ai':
        return { texte: noms.agent(cfg.ai_agent_id) ?? node.label, ia: true }
      case 'route_agent':
        return { texte: node.label || t('manager.numbers.who.oneAgent', 'Un membre de l’équipe'), suite: sortie(node, 'no_answer'), suiteQuand: t('manager.numbers.who.nobodyAnswers', 'Personne ne décroche') }
      case 'route_number':
        return { texte: t('manager.numbers.who.externalNumber', 'Numéro externe {{n}}', { n: String(cfg.phone_number || '') }) }
      case 'voicemail':
        return { texte: t('manager.numbers.who.voicemail', 'Messagerie vocale') }
      case 'menu':
        return { texte: t('manager.numbers.who.menu', 'Menu à touches ({{n}} choix)', { n: Array.isArray(cfg.options) ? cfg.options.length : 0 }) }
      case 'message':
      case 'language_select':
      case 'sms':
        return cibleDe(premiereSortie(node))
      default:
        return { texte: node.label }
    }
  }

  const parcourir = (node: IvrNode | undefined, quand: string) => {
    if (!node || vus.has(node.node_id + quand)) return
    vus.add(node.node_id + quand)
    if (node.node_type === 'start') return parcourir(premiereSortie(node), quand)
    if (node.node_type === 'time_check') {
      parcourir(sortie(node, 'open'), t('manager.numbers.who.duringHours', "Heures d'ouverture"))
      parcourir(sortie(node, 'closed'), t('manager.numbers.who.afterHours', 'Hors des heures'))
      return
    }
    if (node.node_type === 'message' || node.node_type === 'language_select' || node.node_type === 'sms') {
      return parcourir(premiereSortie(node), quand)
    }
    let c = cibleDe(node)
    etapes.push({ quand, cible: c.texte, ia: c.ia })
    // Personne ne décroche → File d'attente → Personne ne se libère → messagerie.
    for (let n = 0; c.suite && c.suiteQuand && n < 3; n++) {
      const s = cibleDe(c.suite)
      etapes.push({ quand: c.suiteQuand, cible: s.texte, ia: s.ia })
      c = s
    }
  }

  const start = flux.nodes.find((n) => n.node_type === 'start')
  parcourir(start, t('manager.numbers.who.incoming', 'Appel entrant'))
  return etapes
}

export function QuiRepond({ etapes }: { etapes: Etape[] }) {
  const { t } = useTranslation()
  if (!etapes.length) return null
  const icone = (quand: string) =>
    quand === t('manager.numbers.who.duringHours', "Heures d'ouverture") || quand === t('manager.numbers.who.afterHours', 'Hors des heures')
      ? Clock
      : quand === t('manager.numbers.who.nobodyAnswers', 'Personne ne décroche')
        ? Users
        : quand === t('manager.numbers.who.nobodyFrees', 'Personne ne se libère')
          ? Hourglass
          : PhoneIncoming
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
      {etapes.map((e, i) => {
        const Icone = icone(e.quand)
        return (
          <div key={i} className="flex items-center gap-3 px-4 h-11 text-sm">
            <Icone className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <span className="w-44 flex-shrink-0 text-slate-500 dark:text-slate-400">{e.quand}</span>
            <ChevronRight className="h-3.5 w-3.5 text-slate-300 flex-shrink-0" />
            <span className={cn('flex items-center gap-1.5 font-medium truncate', e.ia ? 'text-purple-700 dark:text-purple-300' : 'text-slate-900 dark:text-white')}>
              {e.ia && <Bot className="h-3.5 w-3.5" />}
              {e.cible}
            </span>
          </div>
        )
      })}
    </div>
  )
}
